// Per-section + overall PQ completion calculations.
//
// Used by the topbar (overall %) and the SideNav (per-section progress bar).
// The legacy logic was binary (every visible field filled = complete, else
// 0%) and ignored custom-renderer sections — which made the % feel broken.

import { pqSections } from '../schema/pqSections.js';

export function isFilled(v) {
  if (v === undefined || v === null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'number') return !isNaN(v);
  if (typeof v === 'boolean') return v;
  if (Array.isArray(v)) return v.some(isFilled);
  if (typeof v === 'object') return Object.values(v).some(isFilled);
  return false;
}

// Collect every field this section is *actually* asking the user for,
// taking conditional blocks (Add Spouse / Add Children) and footer fields
// into account.
function visibleFields(section, flags) {
  let fields = (section.fields || []).filter(
    f => !f.showIf || flags[f.showIf]
  );
  if (Array.isArray(section.conditionalBlocks)) {
    section.conditionalBlocks.forEach(block => {
      if (flags[block.flag] && Array.isArray(block.fields)) {
        fields = fields.concat(block.fields);
      }
    });
  }
  if (Array.isArray(section.afterConditional)) {
    section.afterConditional.forEach(block => {
      if (flags[block.flag] && Array.isArray(block.summaryFields)) {
        fields = fields.concat(block.summaryFields);
      }
    });
  }
  if (Array.isArray(section.footerFields)) {
    fields = fields.concat(section.footerFields);
  }
  return fields;
}

// Count populated leaf values inside a section slice (recursive). Ignores
// internal/meta keys that start with `_`.
function countFilledLeaves(obj) {
  if (obj == null) return 0;
  if (Array.isArray(obj)) {
    return obj.reduce((sum, item) => sum + countFilledLeaves(item), 0);
  }
  if (typeof obj !== 'object') return isFilled(obj) ? 1 : 0;
  // Untouched app defaults don't count toward progress — only data that traces
  // back to real advisor input does:
  //   _seeded   — starterRow defaults (e.g. Relationships' pre-filled roles):
  //               nobody typed these, so they stay excluded.
  //   _readOnly — auto-DERIVED fields (a mortgage flowing from Assets into
  //               Liabilities): these exist only because the advisor entered
  //               the source, so they DO count. Excluding them made a
  //               Liabilities section visibly full of rows read "0% complete"
  //               (UAT: Eugene, 7/8).
  const skip = new Set(Array.isArray(obj._seeded) ? obj._seeded : []);
  let count = 0;
  for (const [k, v] of Object.entries(obj)) {
    if (k.startsWith('_')) continue;
    if (skip.has(k)) continue;
    count += countFilledLeaves(v);
  }
  return count;
}

// Target leaf count for custom-renderer sections. Roughly: "how many
// populated values does a typically-complete section have?" — used as
// the denominator to convert raw leaf counts to a 0-100 percentage.
const CUSTOM_TARGETS = {
  employment: 6,
  assets: 10,
  liabilities: 5,
  income: 6,
  taxesExpenses: 6,
};

// Per-section progress as a 0-100 integer.
export function sectionProgress(section, data = {}) {
  const sectionData = data[section.id] || {};
  const flags = data._flags || {};

  const fields = visibleFields(section, flags);

  if (fields.length > 0) {
    const filled = fields.filter(f => isFilled(sectionData[f.key])).length;
    let pct = (filled / fields.length) * 100;

    // Bonus credit when a section has tables (Relationships, Insurance,
    // Children/Grandchildren) and at least one row has data.
    const tables = section.tables || [];
    if (tables.length > 0) {
      const anyRow = tables.some(t =>
        Array.isArray(sectionData[t.key]) && sectionData[t.key].some(isFilled)
      );
      if (anyRow) pct = Math.min(100, pct + (100 / (fields.length + 1)));
    }

    return Math.round(pct);
  }

  // Custom-renderer sections: scan all data, scale against the target.
  const target = CUSTOM_TARGETS[section.id] || 5;
  const filled = countFilledLeaves(sectionData);
  if (filled === 0) return 0;
  return Math.min(100, Math.round((filled / target) * 100));
}

// Average per-section progress, rounded to an integer 0-100.
export function overallProgress(data = {}) {
  if (pqSections.length === 0) return 0;
  const total = pqSections.reduce(
    (sum, s) => sum + sectionProgress(s, data),
    0
  );
  return Math.round(total / pqSections.length);
}
